import React, { useMemo, useState } from 'react';
import { Store, Province, StoreTier } from '../../data/types';
import { SearchIcon, MapPinIcon, BuildingIcon, ArrowRightIcon, FilterIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface StoreAvailabilityBrowserProps {
  stores: Store[];
  onRequestSpace: (storeId?: string, weekNumber?: number) => void;
}
export function StoreAvailabilityBrowser({
  stores,
  onRequestSpace
}: StoreAvailabilityBrowserProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvince, setSelectedProvince] = useState<Province | 'all'>('all');
  const [selectedTier, setSelectedTier] = useState<StoreTier | 'all'>('all');
  // Extract unique weeks
  const weeks = useMemo(() => {
    const weekMap = new Map<number, {
      start: string;
      end: string;
    }>();
    stores.forEach(store => {
      store.slots.forEach(slot => {
        if (!weekMap.has(slot.weekNumber)) {
          weekMap.set(slot.weekNumber, {
            start: slot.weekStartDate,
            end: slot.weekEndDate
          });
        }
      });
    });
    return Array.from(weekMap.entries()).map(([num, data]) => ({
      weekNumber: num,
      ...data
    })).sort((a, b) => a.weekNumber - b.weekNumber);
  }, [stores]);
  // Filter stores and calculate availability
  const filteredStores = useMemo(() => {
    return stores.filter(store => {
      if (selectedProvince !== 'all' && store.province !== selectedProvince) return false;
      if (selectedTier !== 'all' && store.tier !== selectedTier) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return store.name.toLowerCase().includes(query) || store.location.toLowerCase().includes(query);
      }
      return true;
    }).map(store => {
      // Calculate availability per week
      const availabilityByWeek: Record<number, {
        available: number;
        total: number;
      }> = {};
      weeks.forEach(w => {
        const slotsThisWeek = store.slots.filter(s => s.weekNumber === w.weekNumber);
        const availableSlots = slotsThisWeek.filter(s => s.status === 'available');
        availabilityByWeek[w.weekNumber] = {
          available: availableSlots.length,
          total: slotsThisWeek.length
        };
      });
      return {
        ...store,
        availabilityByWeek,
        totalAvailable: Object.values(availabilityByWeek).reduce((sum, w) => sum + w.available, 0)
      };
    }).sort((a, b) => b.totalAvailable - a.totalAvailable); // Sort by most availability first
  }, [stores, selectedProvince, selectedTier, searchQuery, weeks]);
  const provinces = useMemo(() => {
    const set = new Set<Province>();
    stores.forEach(s => set.add(s.province));
    return Array.from(set).sort();
  }, [stores]);
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short'
    });
  };
  return <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden flex flex-col" data-id="element-502">
      {/* Header & Filters */}
      <div className="px-5 py-4 border-b border-border bg-surface-warm space-y-4" data-id="element-503">
        <div className="flex items-center justify-between" data-id="element-504">
          <div data-id="element-505">
            <h3 className="text-base font-bold text-text-primary flex items-center gap-2" data-id="element-506">
              <SearchIcon className="w-4 h-4 text-text-secondary" data-id="element-507" />
              Browse Store Availability
            </h3>
            <p className="text-xs text-text-secondary mt-0.5" data-id="element-508">
              Find open media slots across the network and request space
              instantly.
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={() => onRequestSpace()} className="flex items-center gap-1.5" data-id="element-509">
            Request Space
            <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-510" />
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3" data-id="element-511">
          <div className="relative flex-1" data-id="element-512">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" data-id="element-513" />
            <input type="text" placeholder="Search stores or locations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all" data-id="element-514" />
          </div>

          <div className="flex items-center gap-2" data-id="element-515">
            <FilterIcon className="w-4 h-4 text-text-tertiary hidden sm:block" data-id="element-516" />
            <select value={selectedProvince} onChange={e => setSelectedProvince(e.target.value as Province | 'all')} className="px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white min-w-[140px]" data-id="element-517">
              <option value="all" data-id="element-518">All Provinces</option>
              {provinces.map(p => <option key={p} value={p} data-id="element-519">
                  {p}
                </option>)}
            </select>

            <select value={selectedTier} onChange={e => setSelectedTier(e.target.value as StoreTier | 'all')} className="px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white min-w-[130px]" data-id="element-520">
              <option value="all" data-id="element-521">All Tiers</option>
              <option value="flagship" data-id="element-522">Flagship</option>
              <option value="high-traffic" data-id="element-523">High Traffic</option>
              <option value="standard" data-id="element-524">Standard</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto" data-id="element-525">
        <table className="w-full text-left border-collapse min-w-max" data-id="element-526">
          <thead data-id="element-527">
            <tr className="bg-surface border-b border-border" data-id="element-528">
              <th className="px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide sticky left-0 bg-surface z-10 border-r border-border shadow-[2px_0_4px_-2px_rgba(0,0,0,0.04)] min-w-[200px]" data-id="element-529">
                Store
              </th>
              {weeks.map(w => <th key={w.weekNumber} className="px-2 py-3 text-center border-r border-border min-w-[64px]" data-id="element-530">
                  <div className="text-[10px] font-semibold text-text-primary" data-id="element-531">
                    Wk {w.weekNumber}
                  </div>
                  <div className="text-[9px] text-text-tertiary font-normal" data-id="element-532">
                    {formatDate(w.start)}
                  </div>
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-id="element-533">
            {filteredStores.length === 0 ? <tr data-id="element-534">
                <td colSpan={weeks.length + 1} className="px-6 py-12 text-center" data-id="element-535">
                  <p className="text-sm text-text-secondary" data-id="element-536">
                    No stores match your filters.
                  </p>
                  <Button variant="ghost" size="sm" className="mt-2" onClick={() => {
                setSearchQuery('');
                setSelectedProvince('all');
                setSelectedTier('all');
              }} data-id="element-537">
                    Clear Filters
                  </Button>
                </td>
              </tr> : filteredStores.map(store => <tr key={store.id} className="hover:bg-surface/40 transition-colors group" data-id="element-538">
                  <td className="px-4 py-3 sticky left-0 bg-white group-hover:bg-surface/40 z-10 border-r border-border shadow-[2px_0_4px_-2px_rgba(0,0,0,0.04)]" data-id="element-539">
                    <div className="flex items-start justify-between gap-2" data-id="element-540">
                      <div data-id="element-541">
                        <div className="text-sm font-medium text-text-primary" data-id="element-542">
                          {store.name}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1" data-id="element-543">
                          <Badge variant={store.tier} className="text-[9px] px-1.5 py-0" data-id="element-544">
                            {store.tier.replace('-', ' ')}
                          </Badge>
                          <span className="text-[10px] text-text-tertiary truncate max-w-[100px]" data-id="element-545">
                            {store.province}
                          </span>
                        </div>
                      </div>
                      <button onClick={() => onRequestSpace(store.id)} className="opacity-0 group-hover:opacity-100 p-1.5 text-primary hover:bg-primary/10 rounded transition-all" title={`Request space at ${store.name}`} data-id="element-546">
                        <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-547" />
                      </button>
                    </div>
                  </td>

                  {weeks.map(w => {
              const status = store.availabilityByWeek[w.weekNumber];
              const isAvailable = status.available > 0;
              const isFullyBooked = status.total > 0 && status.available === 0;
              return <td key={w.weekNumber} className="p-1.5 border-r border-border text-center align-middle" data-id="element-548">
                        <button onClick={() => isAvailable ? onRequestSpace(store.id, w.weekNumber) : undefined} disabled={!isAvailable} className={`w-full h-8 rounded flex items-center justify-center transition-all ${isAvailable ? 'bg-available/10 hover:bg-available/20 cursor-pointer border border-available/20' : isFullyBooked ? 'bg-surface-warm/50 cursor-not-allowed' : 'bg-transparent'}`} title={isAvailable ? `${status.available} slots available. Click to request.` : 'Fully booked'} data-id="element-549">
                          {isAvailable ? <span className="text-[10px] font-bold text-available-dark" data-id="element-550">
                              {status.available}
                            </span> : isFullyBooked ? <div className="w-1.5 h-1.5 rounded-full bg-text-tertiary/40" data-id="element-551" /> : null}
                        </button>
                      </td>;
            })}
                </tr>)}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-border bg-surface flex items-center gap-4 text-[10px] text-text-secondary" data-id="element-552">
        <div className="flex items-center gap-1.5" data-id="element-553">
          <div className="w-3 h-3 rounded bg-available/10 border border-available/20 flex items-center justify-center" data-id="element-554">
            <span className="text-[8px] font-bold text-available-dark" data-id="element-555">1</span>
          </div>
          <span data-id="element-556">Available slots</span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-557">
          <div className="w-3 h-3 rounded bg-surface-warm/50 flex items-center justify-center" data-id="element-558">
            <div className="w-1 h-1 rounded-full bg-text-tertiary/40" data-id="element-559" />
          </div>
          <span data-id="element-560">Fully booked</span>
        </div>
      </div>
    </div>;
}